import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { user, profileData, loading } = useAuth();

  debugLogger.debug('OnboardingGuard - Status check:', {
    hasUser: !!user,
    hasProfileData: !!profileData,
    onboardingCompleted: profileData?.onboarding_completed,
    loading,
    currentPath: window.location.pathname
  });

  // If still loading authentication data, show nothing (parent will handle loading state)
  if (loading) {
    debugLogger.debug('OnboardingGuard - Still loading auth data');
    return null;
  }

  // If no user, redirect to auth (but this should be handled by parent components)
  if (!user) {
    debugLogger.debug('OnboardingGuard - No user found, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  // If user exists but onboarding is not completed, redirect to onboarding
  if (user && profileData?.onboarding_completed === false) {
    debugLogger.debug('OnboardingGuard - Onboarding not completed, redirecting to onboarding');
    return <Navigate to="/main/onboarding" replace />;
  }

  // If onboarding is completed or profile data is not loaded yet, render children
  debugLogger.debug('OnboardingGuard - All checks passed, rendering children');
  return <>{children}</>;
};